import { useState, useEffect, useCallback } from 'react';
import {
  MapPin,
  Sun,
  DollarSign,
  Edit3,
  Eye,
  Trash2,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Zap,
  Building2,
  Factory,
  Home,
  TreePine,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../types';
import { useNavigate } from 'react-router-dom';

interface Project {
  id: string;
  user_type: string;
  project_status: string;
  created_at: string;
  updated_at: string;
  location_data?: {
    address?: string;
    locations?: Array<{
      address?: string;
      latitude?: number;
      longitude?: number;
    }>;
  };
  solar_design?: {
    system_size?: number;
    panel_count?: number;
    yearly_production?: number;
    offset?: number;
  };
  bill_data?: {
    monthly_usage?: number;
    monthly_bill?: number;
    yearly_consumption?: number;
  };
  ai_offer?: any;
}

interface YourProjectsProps {
  user: UserProfile;
}

const getStatusInfo = (status: string) => {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        dotColor: 'bg-green-500'
      };
    case 'in-progress':
      return {
        label: 'In Progress',
        color: 'bg-blue-100 text-blue-800',
        icon: Clock,
        dotColor: 'bg-blue-500'
      };
    case 'pending':
    default:
      return {
        label: 'Pending',
        color: 'bg-yellow-100 text-yellow-800',
        icon: AlertCircle,
        dotColor: 'bg-yellow-500'
      };
  }
};

const getUserTypeInfo = (userType: string) => {
  switch (userType) {
    case 'residential':
      return {
        label: 'Residential',
        icon: Home,
        color: 'bg-blue-50 text-blue-700',
        bgColor: 'bg-blue-50'
      };
    case 'commercial':
      return {
        label: 'Commercial',
        icon: Building2,
        color: 'bg-purple-50 text-purple-700',
        bgColor: 'bg-purple-50'
      };
    case 'industrial':
      return {
        label: 'Industrial',
        icon: Factory,
        color: 'bg-orange-50 text-orange-700',
        bgColor: 'bg-orange-50'
      };
    case 'agricultural':
      return {
        label: 'Agricultural',
        icon: TreePine,
        color: 'bg-green-50 text-green-700',
        bgColor: 'bg-green-50'
      };
    default:
      return {
        label: 'Unknown',
        icon: Home,
        color: 'bg-gray-50 text-gray-700',
        bgColor: 'bg-gray-50'
      };
  }
};

export function YourProjects({ user }: YourProjectsProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setProjects(data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleEditProject = (project: Project) => {
    // Navigate to project form with the project ID
    navigate(`/project/${project.id}`);
  };

  const handleViewProject = (project: Project) => {
    // Navigate to project details
    navigate(`/project/${project.id}/details`);
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getProjectLocation = (project: Project) => {
    if (project.location_data?.address) {
      return project.location_data.address;
    }
    if (project.location_data?.locations?.[0]?.address) {
      return project.location_data.locations[0].address;
    }
    return 'Location not specified';
  };

  const getSystemSize = (project: Project) => {
    return project.solar_design?.system_size || 0;
  };

  const getMonthlyBill = (project: Project) => {
    return project.bill_data?.monthly_bill || 0;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-100 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Projects</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchProjects}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Your Projects</h2>
            <p className="text-gray-600 mt-1">
              {projects.length} {projects.length === 1 ? 'project' : 'projects'} total
            </p>
          </div>
          <button
            onClick={() => navigate('/project/new')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <Plus className="h-5 w-5" />
            New Project
          </button>
        </div>
      </div>

      {/* Projects List */}
      <div className="p-6">
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sun className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Projects Yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Start your solar journey by creating your first project. Get personalized recommendations and detailed analysis.
            </p>
            <button
              onClick={() => navigate('/project/new')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center gap-2 mx-auto shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus className="h-5 w-5" />
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {projects.map((project) => {
              const statusInfo = getStatusInfo(project.project_status);
              const userTypeInfo = getUserTypeInfo(project.user_type);
              const UserTypeIcon = userTypeInfo.icon;

              return (
                <div
                  key={project.id}
                  className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors duration-200 border border-gray-200 hover:border-gray-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Project Header */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2 rounded-lg ${userTypeInfo.bgColor}`}>
                          <UserTypeIcon className={`h-5 w-5 ${userTypeInfo.color}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {userTypeInfo.label} Solar Project
                          </h3>
                          <p className="text-sm text-gray-600">
                            Created {formatDate(project.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Project Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span className="truncate">{getProjectLocation(project)}</span>
                        </div>
                        
                        {getSystemSize(project) > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Zap className="h-4 w-4" />
                            <span>{getSystemSize(project)} kW System</span>
                          </div>
                        )}
                        
                        {getMonthlyBill(project) > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <DollarSign className="h-4 w-4" />
                            <span>${getMonthlyBill(project).toLocaleString()}/month</span>
                          </div>
                        )}
                      </div>

                      {/* Status */}
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${statusInfo.dotColor}`}></div>
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleViewProject(project)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditProject(project)}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit Project"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete Project"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}



